import React from 'react';
import GroupedFlatTableView from './GroupedFlatTableView';
import { AllProjectsView } from './AllProjectsView';
import { ProjectTasksView } from './ProjectTasksView';
import { EmptyStateCard } from './EmptyStateCard';
import { ProjectsNoMatchesState } from './ProjectsNoMatchesState';
import { WalkthroughChoiceScreen } from '@/features/walkthrough';
import './GroupedFlatTableView.css';
import './EmptyStateCard.css';

export const HomePageContent = ({
    viewType,
    filteredApps,
    allApps,
    allUsecases,
    filterValue,
    searchValue,
    selectedProject,
    onOpenProject,
    onCloseProject,
    onDeleteUsecase,
    onBulkDeleteUsecases,
    onBulkMoveUsecases,
    onDeleteProject,
    onEditProject,
    onModelDeployment,
    onMoveToScreen,
    onNewTask,
    onDuplicateUsecase,
    onEditTask,
    onCreateProject,
    isLoading = false,
    isFullPageEmpty = false,
}) => {
    // Both views consume the same sub_groups shape so the List View can
    // group sub-tasks under their parent composed task (matching the cards).
    const groupedProjects = filteredApps.map(app => ({
        group_name: app.group_name,
        project_goal: app.project_goal,
        sub_groups: app.sub_groups || [],
    }));

    const isDrilledIn = viewType === 'cards' && !!selectedProject;

    // Drilled-in project always keeps its back header — including when the
    // active search/filters match nothing (or the last matching task was
    // moved out). The page-level empty state has no back control, so it
    // must not replace this view.
    if (isDrilledIn) {
        const app =
            groupedProjects.find(a => a.group_name === selectedProject) || null;
        return (
            <div className="home-page-cards-page">
                <ProjectTasksView
                    app={app}
                    projectName={selectedProject}
                    allApps={allApps}
                    isLoading={isLoading}
                    searchValue={searchValue}
                    onBack={onCloseProject}
                    onDeleteUsecase={onDeleteUsecase}
                    onBulkDeleteUsecases={onBulkDeleteUsecases}
                    onBulkMoveUsecases={onBulkMoveUsecases}
                    onDeleteProject={onDeleteProject}
                    onEditProject={onEditProject}
                    onModelDeployment={onModelDeployment}
                    onNewTask={onNewTask}
                    onDuplicateUsecase={onDuplicateUsecase}
                    onEditTask={onEditTask}
                />
            </div>
        );
    }

    if (groupedProjects.length === 0) {
        // Projects exist but search/filters matched nothing — show the
        // no-matches zero-state (no CTA) instead of the onboarding card.
        if (!isFullPageEmpty && !isLoading) {
            return (
                <div className="home-page-cards-page">
                    <ProjectsNoMatchesState searchValue={searchValue} />
                </div>
            );
        }

        // A workspace with no tasks always offers the guided walkthrough
        // ("Show me how") next to today's flow ("Start building"), whatever
        // the user picked before — the tour can be restarted from here, and
        // "Back" from the wizard lands on the same screen. Keep the (spinner)
        // empty card while projects load so the choice never flashes for a
        // user who does have projects.
        const showWalkthroughChoice = isFullPageEmpty && !isLoading;
        return (
            <div
                className={
                    isFullPageEmpty
                        ? 'home-page-empty-state home-page-empty-state--full'
                        : 'home-page-empty-state'
                }
            >
                {showWalkthroughChoice ? (
                    <WalkthroughChoiceScreen onCreateProject={onCreateProject} />
                ) : (
                    <EmptyStateCard
                        onCreateProject={onCreateProject}
                        primaryActionDisableWhenReadOnly
                        isLoading={isLoading}
                    />
                )}
            </div>
        );
    }

    if (viewType === 'cards') {
        // All-projects level — one summary card per project, with marquee select.
        return (
            <div className="home-page-cards-page">
                <AllProjectsView
                    groupedProjects={groupedProjects}
                    allApps={allApps}
                    onOpenProject={onOpenProject}
                    onEditProject={onEditProject}
                    onDeleteProject={onDeleteProject}
                />
            </div>
        );
    }

    return ( // list view
        <div style={{ padding: '32px 0' }}>
            <GroupedFlatTableView
                groupedProjects={groupedProjects}
                allApps={allApps}
                moveToScreen={onMoveToScreen}
                onDeleteUsecase={onDeleteUsecase}
                onBulkDeleteUsecases={onBulkDeleteUsecases}
                onBulkMoveUsecases={onBulkMoveUsecases}
                onModelDeployment={onModelDeployment}
                onDuplicateUsecase={onDuplicateUsecase}
                onEditProject={onEditProject}
                onDeleteProject={onDeleteProject}
                onNewTask={onNewTask}
                onEditTask={onEditTask}
            />
        </div>
    );
}; 